import * as THREE from 'three/webgpu';
import {
    isSketchArcObject,
    isSketchCircleObject,
    isSketchEllipseObject,
    isSketchLineObject,
    isSketchParabolaObject,
    isSketchPointObject,
    isSketchSlotObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
} from '../../mesh/guards';
import type {ISketchObject} from '../../mesh/sketch/ISketchObject';
import {EllipseControlPointRole} from '../../mesh/sketch/ISketchObject';
import type {SketchPointObject} from '../../mesh/sketch/SketchPointObject';
import type {SketchSlotObject} from '../../mesh/sketch/SketchSlotObject';
import type {SketchSplineObject} from '../../mesh/sketch/SketchSplineObject';
import type {SketchStyleSplineObject} from '../../mesh/sketch/SketchStyleSplineObject';
import type {AbstractSketchEdgeObject} from '../../mesh/sketch/AbstractSketchEdgeObject';
import type {SketchArcObject} from '../../mesh/sketch/SketchArcObject';
import type {SketchEllipseObject} from '../../mesh/sketch/SketchEllipseObject';
import type {ReferencePlane} from '../../reference/ReferencePlane';
import {SketchEntityDefinitionAdapter} from './SketchEntityDefinitionAdapter';
import type {
    SketchMirrorAxisResolvedGeometry,
} from '../../../types/sketch/mirror/SketchMirrorAxisReferenceTypes';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../../sketch/config/SketchGeometryToleranceConfig';

/**
 * 草图实体镜像定义适配器。
 *
 * 职责与边界：
 * 1. 只接纳已有完整定义读写协议的实体类型，未知边类型必须显式扩展，禁止退化为散点镜像；
 * 2. 每次实体提交只预计算一次轴线标量，控制点热路径复用向量，避免每点创建投影与方向临时对象；
 * 3. 复杂曲线按完整参数、控制点顺序及元数据原子写回，调用方只负责选择驱动侧与求解排队。
 *
 * 生命周期：由一条实体对称关系长期持有；不创建渲染资源，无需释放。
 */
export class SketchMirrorEntityGeometryAdapter extends SketchEntityDefinitionAdapter {
    /** 镜像轴起点 X。 */
    private axisStartX = 0;
    /** 镜像轴起点 Y。 */
    private axisStartY = 0;
    /** 镜像轴起点 Z。 */
    private axisStartZ = 0;
    /** 镜像轴方向 X。 */
    private axisDirectionX = 0;
    /** 镜像轴方向 Y。 */
    private axisDirectionY = 0;
    /** 镜像轴方向 Z。 */
    private axisDirectionZ = 0;
    /** 镜像轴方向长度平方的倒数。 */
    private axisInverseLengthSquared = 0;
    /** 单点即时写回复用向量。 */
    private readonly reflectedPointScratch = new THREE.Vector3();
    /** 曲线中心写回复用向量。 */
    private readonly reflectedCenterScratch = new THREE.Vector3();
    /** 抛物线焦点写回复用向量。 */
    private readonly reflectedFocalScratch = new THREE.Vector3();
    /** 抛物线顶点写回复用向量。 */
    private readonly reflectedApexScratch = new THREE.Vector3();
    /** 样条控制点写回数组，仅在控制点数量增长时扩容。 */
    private readonly splineControlPointScratch: THREE.Vector3[] = [];
    /** 方向角镜像前的世界点。 */
    private readonly directionWorldScratch = new THREE.Vector3();
    /** 方向角镜像后的世界点。 */
    private readonly reflectedDirectionWorldScratch = new THREE.Vector3();
    /** 方向角驱动侧局部坐标。 */
    private readonly driverLocalScratch = new THREE.Vector2();
    /** 方向角被驱动侧局部坐标。 */
    private readonly drivenLocalScratch = new THREE.Vector2();
    /** 方向角被驱动中心局部坐标。 */
    private readonly drivenCenterLocalScratch = new THREE.Vector2();

    /**
     * 判断实体是否还有不属于成员边的顶层派生几何需要在批回写末尾刷新。
     * @param entity 待判断实体。
     * @returns 顶层复合轮廓需要独立刷新时返回 true。
     */
    public requiresPostSolveDefinitionRefresh(entity: ISketchObject): boolean {
        return isSketchArcObject(entity)
            || isSketchEllipseObject(entity)
            || isSketchParabolaObject(entity)
            || isSketchSplineObject(entity)
            || isSketchStyleSplineObject(entity)
            || isSketchSlotObject(entity);
    }

    /**
     * 返回由后求解定义操作统一刷新的实体自有边。
     *
     * 只延后当前实体真正拥有的边；共享端点上的其它草图边仍由批处理引擎正常刷新。
     * @param entity 被驱动实体。
     * @returns 需要跳过通用中间刷新的边。
     */
    public resolvePostSolveRefreshEdges(entity: ISketchObject): readonly AbstractSketchEdgeObject[] {
        if (isSketchSlotObject(entity)) {
            const constructionEdge = entity.isArcSlot()
                ? entity.constructionCenterlineArc
                : entity.constructionCenterline;
            return constructionEdge ? [...entity.getEdges(), constructionEdge] : entity.getEdges();
        }
        if (
            isSketchArcObject(entity)
            || isSketchEllipseObject(entity)
            || isSketchParabolaObject(entity)
            || isSketchSplineObject(entity)
            || isSketchStyleSplineObject(entity)
        ) {
            return [entity];
        }
        return [];
    }

    /**
     * 在成员控制点与边均完成批回写后刷新复合实体派生几何。
     * @param driverEntity 当前驱动实体。
     * @param drivenEntity 已完成点位求解的被驱动实体。
     * @param axisGeometry 当前世界镜像轴。
     * @throws 传入非复合实体时抛出错误，避免调用方静默吞掉协议误用。
     */
    public refreshPostSolveDefinition(
        driverEntity: ISketchObject,
        drivenEntity: ISketchObject,
        axisGeometry: SketchMirrorAxisResolvedGeometry,
    ): void {
        if (!this.prepareAxis(axisGeometry)) {
            throw new Error('镜像复合实体定义刷新失败：镜像轴退化。');
        }
        /**
         * 驱动侧在点批处理写回后可能才完成参数域归一（如部分椭圆端点吸附）。
         * 此时必须从最终驱动定义原子重建被驱动侧，不能继续消费归一前的鼠标点请求。
         * 调用方会在整批控制点落位后执行本入口，因此其它共享边只会观察到完整终态。
         */
        if (!this.applyPrepared(driverEntity, drivenEntity)) {
            throw new Error('镜像复合实体定义刷新失败：驱动侧与被驱动侧定义协议不匹配。');
        }
    }

    /**
     * 解析反射手性下与驱动控制点一一对应的目标控制点。
     *
     * 椭圆长轴正负角色保持同名，但反射会翻转局部坐标手性，因此 `minor+` 必须对应 `minor-`；
     * 其它实体沿用稳定定义顺序。
     * @param driverEntity 当前驱动实体。
     * @param drivenEntity 当前被驱动实体。
     * @returns 与驱动控制点定义顺序一一配对的目标点域。
     */
    public resolveMirroredControlPoints(
        driverEntity: ISketchObject,
        drivenEntity: ISketchObject,
    ): SketchPointObject[] {
        if (!isSketchEllipseObject(driverEntity) || !isSketchEllipseObject(drivenEntity)) {
            return this.resolveControlPoints(drivenEntity);
        }
        const driverCardinalPoints = driverEntity.getCardinalControlPointsSnapshot();
        const drivenCardinalPoints = drivenEntity.getCardinalControlPointsSnapshot();
        const mirroredRoleMap = new Map([
            [EllipseControlPointRole.MajorPositive, EllipseControlPointRole.MajorPositive],
            [EllipseControlPointRole.MajorNegative, EllipseControlPointRole.MajorNegative],
            [EllipseControlPointRole.MinorPositive, EllipseControlPointRole.MinorNegative],
            [EllipseControlPointRole.MinorNegative, EllipseControlPointRole.MinorPositive],
        ]);

        return this.resolveControlPoints(driverEntity).map((driverPoint) => {
            if (driverPoint === driverEntity.getCenterPoint()) {
                return drivenEntity.getCenterPoint();
            }
            if (driverPoint === driverEntity.getStartPoint()) {
                return drivenEntity.getStartPoint();
            }
            if (driverPoint === driverEntity.getEndPoint()) {
                return drivenEntity.getEndPoint();
            }
            for (const [driverRole, cardinalPoint] of driverCardinalPoints) {
                if (cardinalPoint !== driverPoint) {
                    continue;
                }
                const drivenRole = mirroredRoleMap.get(driverRole);
                const drivenPoint = drivenRole ? drivenCardinalPoints.get(drivenRole) : null;
                if (drivenPoint) {
                    return drivenPoint;
                }
            }
            throw new Error('镜像椭圆失败：无法按控制点角色解析被驱动点。');
        });
    }

    /**
     * 预计算当前无限轴线的二维反射标量。
     * @param axisGeometry 世界坐标镜像轴。
     * @returns 轴线有效时返回 true。
     */
    public prepareAxis(axisGeometry: SketchMirrorAxisResolvedGeometry): boolean {
        const {startWorld, endWorld} = axisGeometry;
        this.axisStartX = startWorld.x;
        this.axisStartY = startWorld.y;
        this.axisStartZ = startWorld.z;
        this.axisDirectionX = endWorld.x - startWorld.x;
        this.axisDirectionY = endWorld.y - startWorld.y;
        this.axisDirectionZ = endWorld.z - startWorld.z;
        const lengthSquared = this.axisDirectionX * this.axisDirectionX
            + this.axisDirectionY * this.axisDirectionY
            + this.axisDirectionZ * this.axisDirectionZ;
        if (!Number.isFinite(lengthSquared)
            || lengthSquared <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.lineDirectionLengthSquared) {
            this.axisInverseLengthSquared = 0;
            return false;
        }
        this.axisInverseLengthSquared = 1 / lengthSquared;
        return true;
    }

    /**
     * 使用最近一次预计算的轴线把世界点反射到指定输出向量。
     * @param pointWorld 待反射世界点。
     * @param output 承载结果的复用向量，允许与 pointWorld 为同一对象。
     * @returns output 本身。
     */
    public reflectPreparedPointTo(pointWorld: THREE.Vector3, output: THREE.Vector3): THREE.Vector3 {
        const pointX = pointWorld.x;
        const pointY = pointWorld.y;
        const pointZ = pointWorld.z;
        const projectionScale = (
            (pointX - this.axisStartX) * this.axisDirectionX
            + (pointY - this.axisStartY) * this.axisDirectionY
            + (pointZ - this.axisStartZ) * this.axisDirectionZ
        ) * this.axisInverseLengthSquared;
        return output.set(
            this.snapReflectionCoordinate(
                2 * (this.axisStartX + this.axisDirectionX * projectionScale) - pointX,
            ),
            this.snapReflectionCoordinate(
                2 * (this.axisStartY + this.axisDirectionY * projectionScale) - pointY,
            ),
            this.snapReflectionCoordinate(
                2 * (this.axisStartZ + this.axisDirectionZ * projectionScale) - pointZ,
            ),
        );
    }

    /**
     * 清除反射计算中纯浮点运算产生的机器精度尾差。
     * @param value 待归一化坐标。
     * @returns 接近 1e-12 网格时的精确值，否则返回原值。
     */
    private snapReflectionCoordinate(value: number): number {
        const snappedValue = Math.round(value * 1e12) / 1e12;
        const normalizedSnappedValue = snappedValue === 0 ? 0 : snappedValue;
        return Math.abs(value - snappedValue) <= Number.EPSILON * 32
            ? normalizedSnappedValue
            : value;
    }

    /**
     * 将驱动实体的完整定义镜像写入同类型实体。
     * @param driverEntity 当前驱动实体。
     * @param drivenEntity 当前被驱动实体。
     * @param axisGeometry 世界坐标镜像轴。
     * @returns 类型匹配且完整定义提交成功时返回 true。
     */
    public apply(
        driverEntity: ISketchObject,
        drivenEntity: ISketchObject,
        axisGeometry: SketchMirrorAxisResolvedGeometry,
    ): boolean {
        if (!this.prepareAxis(axisGeometry)) {
            return false;
        }
        return this.applyPrepared(driverEntity, drivenEntity);
    }

    /**
     * 使用已预计算轴线提交实体定义。
     * @param driverEntity 当前驱动实体。
     * @param drivenEntity 当前被驱动实体。
     * @returns 类型匹配且写回完成时返回 true。
     */
    private applyPrepared(driverEntity: ISketchObject, drivenEntity: ISketchObject): boolean {
        if (isSketchPointObject(driverEntity) && isSketchPointObject(drivenEntity)) {
            driverEntity.getPositionTo(this.reflectedPointScratch);
            this.reflectPreparedPointTo(this.reflectedPointScratch, this.reflectedPointScratch);
            drivenEntity.syncPositionSilently(this.reflectedPointScratch);
            return true;
        }
        if (isSketchLineObject(driverEntity) && isSketchLineObject(drivenEntity)) {
            driverEntity.startPoint.getPositionTo(this.reflectedPointScratch);
            this.reflectPreparedPointTo(this.reflectedPointScratch, this.reflectedPointScratch);
            drivenEntity.startPoint.syncPositionSilently(this.reflectedPointScratch);
            driverEntity.endPoint.getPositionTo(this.reflectedPointScratch);
            this.reflectPreparedPointTo(this.reflectedPointScratch, this.reflectedPointScratch);
            drivenEntity.endPoint.syncPositionSilently(this.reflectedPointScratch);
            drivenEntity.refreshGeometryOnly();
            return true;
        }
        if (isSketchCircleObject(driverEntity) && isSketchCircleObject(drivenEntity)) {
            this.reflectPreparedPointTo(driverEntity.getCenterWorld(), this.reflectedPointScratch);
            drivenEntity.getCenterControlPoint().syncPositionSilently(this.reflectedPointScratch);
            drivenEntity.setRadiusSilently(driverEntity.radius);
            return true;
        }
        if (isSketchArcObject(driverEntity) && isSketchArcObject(drivenEntity)) {
            this.applyPreparedArcDefinition(driverEntity, drivenEntity);
            drivenEntity.syncControlPointsFromDefinition();
            // 角度三角函数会把整数轴点还原成 1e-16 尾差；控制点仍以源点的直接反射为权威。
            driverEntity.getCenterPoint().getPositionTo(this.reflectedPointScratch);
            this.reflectPreparedPointTo(this.reflectedPointScratch, this.reflectedPointScratch);
            drivenEntity.getCenterPoint().syncPositionSilently(this.reflectedPointScratch);
            driverEntity.getStartPoint().getPositionTo(this.reflectedPointScratch);
            this.reflectPreparedPointTo(this.reflectedPointScratch, this.reflectedPointScratch);
            drivenEntity.getStartPoint().syncPositionSilently(this.reflectedPointScratch);
            driverEntity.getEndPoint().getPositionTo(this.reflectedPointScratch);
            this.reflectPreparedPointTo(this.reflectedPointScratch, this.reflectedPointScratch);
            drivenEntity.getEndPoint().syncPositionSilently(this.reflectedPointScratch);
            return true;
        }
        if (isSketchEllipseObject(driverEntity) && isSketchEllipseObject(drivenEntity)) {
            this.applyPreparedEllipseDefinition(driverEntity, drivenEntity);
            drivenEntity.syncControlPointsFromDefinition();
            return true;
        }
        if (isSketchParabolaObject(driverEntity) && isSketchParabolaObject(drivenEntity)) {
            const definition = driverEntity.getParabolaDefinition();
            drivenEntity.updateParabolaDefinition({
                ...definition,
                focalWorld: this.reflectPreparedPointTo(definition.focalWorld, this.reflectedFocalScratch),
                apexWorld: this.reflectPreparedPointTo(definition.apexWorld, this.reflectedApexScratch),
                startParameter: -definition.startParameter,
                endParameter: -definition.endParameter,
            });
            return true;
        }
        if (isSketchSplineObject(driverEntity) && isSketchSplineObject(drivenEntity)) {
            return this.applyPreparedSpline(driverEntity, drivenEntity);
        }
        if (isSketchStyleSplineObject(driverEntity) && isSketchStyleSplineObject(drivenEntity)) {
            return this.applyPreparedSpline(driverEntity, drivenEntity);
        }
        if (isSketchSlotObject(driverEntity) && isSketchSlotObject(drivenEntity)) {
            return this.applyPreparedSlot(driverEntity, drivenEntity);
        }
        return false;
    }

    /**
     * 只提交镜像后的圆弧参数定义，不重写已由同一求解图收敛的控制点。
     * @param driverEntity 驱动圆弧。
     * @param drivenEntity 被驱动圆弧。
     */
    private applyPreparedArcDefinition(driverEntity: SketchArcObject, drivenEntity: SketchArcObject): void {
        const definition = driverEntity.getArcDefinition();
        const centerWorld = this.reflectPreparedPointTo(definition.centerWorld, this.reflectedCenterScratch);
        drivenEntity.updateArcDefinition({
            ...definition,
            centerWorld,
            startAngle: this.reflectPreparedAngle(
                driverEntity.referencePlane,
                drivenEntity.referencePlane,
                definition.centerWorld,
                centerWorld,
                definition.startAngle,
            ),
            endAngle: this.reflectPreparedAngle(
                driverEntity.referencePlane,
                drivenEntity.referencePlane,
                definition.centerWorld,
                centerWorld,
                definition.endAngle,
            ),
            clockwise: !definition.clockwise,
        });
    }

    /**
     * 只提交镜像后的椭圆参数定义，不重写已由同一求解图收敛的控制点。
     * @param driverEntity 驱动椭圆。
     * @param drivenEntity 被驱动椭圆。
     */
    private applyPreparedEllipseDefinition(driverEntity: SketchEllipseObject, drivenEntity: SketchEllipseObject): void {
        const definition = driverEntity.getEllipseDefinition();
        const centerWorld = this.reflectPreparedPointTo(definition.centerWorld, this.reflectedCenterScratch);
        const reflectedRotation = this.reflectPreparedAngle(
            driverEntity.referencePlane,
            drivenEntity.referencePlane,
            definition.centerWorld,
            centerWorld,
            definition.rotation,
        );
        const reflectedStartDirection = this.reflectPreparedAngle(
            driverEntity.referencePlane,
            drivenEntity.referencePlane,
            definition.centerWorld,
            centerWorld,
            definition.rotation + definition.startAngle,
        );
        const reflectedEndDirection = this.reflectPreparedAngle(
            driverEntity.referencePlane,
            drivenEntity.referencePlane,
            definition.centerWorld,
            centerWorld,
            definition.rotation + definition.endAngle,
        );
        drivenEntity.updateEllipseDefinition({
            ...definition,
            centerWorld,
            rotation: reflectedRotation,
            // 参数角必须相对于镜像后的长轴重新计算；直接取负只适用于特定局部坐标轴。
            startAngle: this.normalizeSignedAngle(reflectedStartDirection - reflectedRotation),
            endAngle: this.normalizeSignedAngle(reflectedEndDirection - reflectedRotation),
            clockwise: !definition.clockwise,
        });
    }

    /**
     * 按有序控制点、采样点和元数据镜像样条定义。
     * @param driverEntity 驱动样条。
     * @param drivenEntity 被驱动样条。
     * @returns 完成原子重建时返回 true。
     */
    private applyPreparedSpline(
        driverEntity: SketchSplineObject | SketchStyleSplineObject,
        drivenEntity: SketchSplineObject | SketchStyleSplineObject,
    ): boolean {
        const driverControlPoints = driverEntity.getControlPoints();
        while (this.splineControlPointScratch.length < driverControlPoints.length) {
            this.splineControlPointScratch.push(new THREE.Vector3());
        }
        const controlPointPositions = this.splineControlPointScratch.slice(0, driverControlPoints.length);
        driverControlPoints.forEach((point, index) => {
            const output = controlPointPositions[index]!;
            point.getPositionTo(output);
            this.reflectPreparedPointTo(output, output);
        });
        const curvePoints = driverEntity.getCurvePoints();
        curvePoints.forEach((point) => this.reflectPreparedPointTo(point, point));
        const curveEvaluationDefinition = driverEntity.getCurveEvaluationDefinition();
        // Worker 求值定义中的极点同样属于权威世界坐标定义；若只镜像显示采样点，
        // 后续精确求值、捕捉和持久化仍会回到源侧，因此必须在同一原子写回中一起反射。
        curveEvaluationDefinition?.controlPoints.forEach((controlPoint) => {
            this.reflectedPointScratch.set(controlPoint.x, controlPoint.y, controlPoint.z);
            this.reflectPreparedPointTo(this.reflectedPointScratch, this.reflectedPointScratch);
            controlPoint.x = this.reflectedPointScratch.x;
            controlPoint.y = this.reflectedPointScratch.y;
            controlPoint.z = this.reflectedPointScratch.z;
        });
        drivenEntity.rebuildByCurveDefinition({
            controlPointPositions,
            curvePoints,
            curveMetadata: driverEntity.getCurveMetadata(),
            curveEvaluationDefinition: curveEvaluationDefinition ?? undefined,
        });
        return true;
    }

    /**
     * 镜像槽口顶层定义及派生控制点。
     * @param driverEntity 驱动槽口。
     * @param drivenEntity 被驱动槽口。
     * @returns 槽口模式一致并完整写回时返回 true。
     */
    private applyPreparedSlot(driverEntity: SketchSlotObject, drivenEntity: SketchSlotObject): boolean {
        if (driverEntity.isArcSlot() !== drivenEntity.isArcSlot()) {
            return false;
        }
        const driverJunctions = driverEntity.getJunctionPointArray();
        const drivenJunctions = drivenEntity.getJunctionPointArray();
        driverJunctions.forEach((point, index) => {
            point.getPositionTo(this.reflectedPointScratch);
            this.reflectPreparedPointTo(this.reflectedPointScratch, this.reflectedPointScratch);
            drivenJunctions[index]?.syncPositionSilently(this.reflectedPointScratch);
        });

        const driverEdges = driverEntity.getEdges();
        const drivenEdges = drivenEntity.getEdges();
        for (let index = 0; index < driverEdges.length; index += 1) {
            const driverEdge = driverEdges[index];
            const drivenEdge = drivenEdges[index];
            if (!driverEdge || !drivenEdge || !this.applyPrepared(driverEdge, drivenEdge)) {
                return false;
            }
        }

        const driverConstruction = driverEntity.isArcSlot()
            ? driverEntity.constructionCenterlineArc
            : driverEntity.constructionCenterline;
        const drivenConstruction = drivenEntity.isArcSlot()
            ? drivenEntity.constructionCenterlineArc
            : drivenEntity.constructionCenterline;
        if (!driverConstruction || !drivenConstruction
            || !this.applyPrepared(driverConstruction, drivenConstruction)) {
            return false;
        }

        if (driverEntity.isArcSlot()) {
            if (!driverEntity.arcCenterPoint || !driverEntity.centerArcMidpoint
                || !drivenEntity.arcCenterPoint || !drivenEntity.centerArcMidpoint) {
                return false;
            }
            driverEntity.arcCenterPoint.getPositionTo(this.reflectedPointScratch);
            this.reflectPreparedPointTo(this.reflectedPointScratch, this.reflectedPointScratch);
            drivenEntity.arcCenterPoint.syncPositionSilently(this.reflectedPointScratch);
            driverEntity.centerArcMidpoint.getPositionTo(this.reflectedPointScratch);
            this.reflectPreparedPointTo(this.reflectedPointScratch, this.reflectedPointScratch);
            drivenEntity.centerArcMidpoint.syncPositionSilently(this.reflectedPointScratch);
        } else {
            if (!driverEntity.centerlineStartPoint || !driverEntity.centerlineEndPoint || !driverEntity.centerPoint
                || !drivenEntity.centerlineStartPoint || !drivenEntity.centerlineEndPoint || !drivenEntity.centerPoint) {
                return false;
            }
            driverEntity.centerlineStartPoint.getPositionTo(this.reflectedPointScratch);
            this.reflectPreparedPointTo(this.reflectedPointScratch, this.reflectedPointScratch);
            drivenEntity.centerlineStartPoint.syncPositionSilently(this.reflectedPointScratch);
            driverEntity.centerlineEndPoint.getPositionTo(this.reflectedPointScratch);
            this.reflectPreparedPointTo(this.reflectedPointScratch, this.reflectedPointScratch);
            drivenEntity.centerlineEndPoint.syncPositionSilently(this.reflectedPointScratch);
            driverEntity.centerPoint.getPositionTo(this.reflectedPointScratch);
            this.reflectPreparedPointTo(this.reflectedPointScratch, this.reflectedPointScratch);
            drivenEntity.centerPoint.syncPositionSilently(this.reflectedPointScratch);
            drivenEntity.constructionCenterline.refreshGeometryOnly();
        }
        // 圆弧中心线已按镜像后的权威圆弧定义完成刷新，避免再次反解产生数值漂移。
        drivenEntity.refreshContourGeometry();
        return true;
    }

    /**
     * 镜像参考面内方向角。
     * @param driverPlane 驱动实体参考面。
     * @param drivenPlane 被驱动实体参考面。
     * @param driverCenter 驱动中心世界点。
     * @param drivenCenter 被驱动中心世界点。
     * @param angle 驱动侧局部方向角。
     * @returns 被驱动侧局部方向角，范围为 [0, 2π)。
     */
    private reflectPreparedAngle(
        driverPlane: ReferencePlane,
        drivenPlane: ReferencePlane,
        driverCenter: THREE.Vector3,
        drivenCenter: THREE.Vector3,
        angle: number,
    ): number {
        driverPlane.worldToLocalTo(driverCenter, this.driverLocalScratch);
        driverPlane.localToWorldTo(
            this.driverLocalScratch.x + Math.cos(angle),
            this.driverLocalScratch.y + Math.sin(angle),
            this.directionWorldScratch,
        );
        this.reflectPreparedPointTo(
            this.directionWorldScratch,
            this.reflectedDirectionWorldScratch,
        );
        drivenPlane.worldToLocalTo(this.reflectedDirectionWorldScratch, this.drivenLocalScratch);
        drivenPlane.worldToLocalTo(drivenCenter, this.drivenCenterLocalScratch);
        const reflectedAngle = Math.atan2(
            this.drivenLocalScratch.y - this.drivenCenterLocalScratch.y,
            this.drivenLocalScratch.x - this.drivenCenterLocalScratch.x,
        );
        return reflectedAngle < 0 ? reflectedAngle + Math.PI * 2 : reflectedAngle;
    }

    /**
     * 把相对参数角归一化到 `[-π, π)`，保持椭圆定义的稳定有符号表达。
     * @param angle 待归一化相对角。
     * @returns 稳定有符号角。
     */
    private normalizeSignedAngle(angle: number): number {
        const fullTurn = Math.PI * 2;
        return ((angle + Math.PI) % fullTurn + fullTurn) % fullTurn - Math.PI;
    }

}
